<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useFollowUpsStore } from 'dashboard/stores/followUps';
import { useAlert } from 'dashboard/composables';
import ConversationApi from 'dashboard/api/conversations';
import { vOnClickOutside } from '@vueuse/components';

const { t } = useI18n();
const router = useRouter();
const followUpsStore = useFollowUpsStore();

const currentUser = useMapGetter('auth/getCurrentUser');
const { isAdminOrSupervisor: isAdmin } = useAdmin();
const accountId = useMapGetter('getCurrentAccountId');
const allLabels = useMapGetter('labels/getLabels');

const conversationLabels = reactive({});
const activeLabelDropdown = ref(null);
const updatingLabelFollowUpId = ref(null);

async function openLabelDropdown(followUp) {
  if (activeLabelDropdown.value === followUp.id) {
    activeLabelDropdown.value = null;
    return;
  }
  activeLabelDropdown.value = followUp.id;
  if (!followUp.conversation_id) return;
  if (conversationLabels[followUp.id] !== undefined) return;
  try {
    const { data } = await ConversationApi.getLabels(followUp.conversation_id);
    conversationLabels[followUp.id] = data.payload ?? [];
  } catch {
    conversationLabels[followUp.id] = [];
  }
}

async function toggleLabel(followUp, labelTitle) {
  if (!followUp.conversation_id) return;
  const current = conversationLabels[followUp.id] ?? [];
  const newLabels = current.includes(labelTitle)
    ? current.filter(l => l !== labelTitle)
    : [...current, labelTitle];
  updatingLabelFollowUpId.value = followUp.id;
  try {
    await ConversationApi.updateLabels(followUp.conversation_id, newLabels);
    conversationLabels[followUp.id] = newLabels;
  } catch {
    useAlert(t('SALES.FOLLOW_UP.ERROR'), { variant: 'error' });
  } finally {
    updatingLabelFollowUpId.value = null;
  }
}

function getLabelColor(title) {
  return allLabels.value.find(l => l.title === title)?.color ?? '#888';
}
// admins see all by default; non-admins always see only their own (enforced on backend too)
const showAll = ref(true);

onMounted(() => {
  loadFollowUps();
});

async function loadFollowUps() {
  const params = {};
  if (!isAdmin.value || !showAll.value) {
    params.agent_id = currentUser.value?.id;
  }
  await followUpsStore.fetchFollowUps(params);
}

async function toggleScope() {
  showAll.value = !showAll.value;
  await loadFollowUps();
}

async function handleMarkDone(id) {
  try {
    await followUpsStore.markDone(id);
    useAlert(t('SALES.FOLLOW_UP.MARK_DONE_SUCCESS'));
  } catch {
    useAlert(t('SALES.FOLLOW_UP.ERROR'), { variant: 'error' });
  }
}

async function handleCancel(id) {
  try {
    await followUpsStore.cancelFollowUp(id);
  } catch {
    useAlert(t('SALES.FOLLOW_UP.ERROR'), { variant: 'error' });
  }
}

function goToConversation(conversationId) {
  if (!conversationId) return;
  router.push({
    name: 'inbox_conversation',
    params: { accountId: accountId.value, conversation_id: conversationId },
  });
}

const creatingId = ref(null);

async function createNewConversation(followUp) {
  if (!followUp.contact_id || !followUp.inbox_id) {
    useAlert(t('SALES.FOLLOW_UP.CREATE_CONVERSATION_ERROR'), { variant: 'error' });
    return;
  }
  creatingId.value = followUp.id;
  try {
    const { data } = await ConversationApi.create({
      inbox_id: followUp.inbox_id,
      contact_id: followUp.contact_id,
      assignee_id: currentUser.value?.id,
    });
    router.push({
      name: 'inbox_conversation',
      params: { accountId: accountId.value, conversation_id: data.id },
    });
  } catch {
    useAlert(t('SALES.FOLLOW_UP.CREATE_CONVERSATION_ERROR'), { variant: 'error' });
  } finally {
    creatingId.value = null;
  }
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const groups = computed(() => [
  { key: 'overdue', label: t('SALES.FOLLOW_UP.OVERDUE'), color: 'text-n-slate-12', icon: 'i-lucide-alert-circle' },
  { key: 'today', label: t('SALES.FOLLOW_UP.TODAY'), color: 'text-n-slate-12', icon: 'i-lucide-clock' },
  { key: 'this_week', label: t('SALES.FOLLOW_UP.THIS_WEEK'), color: 'text-n-brand', icon: 'i-lucide-calendar-days' },
  { key: 'upcoming', label: t('SALES.FOLLOW_UP.UPCOMING'), color: 'text-n-slate-9', icon: 'i-lucide-calendar' },
]);

const collapsed = reactive({ overdue: false, today: false, this_week: false, upcoming: false });
function toggleGroup(key) {
  collapsed[key] = !collapsed[key];
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <h2 class="text-base font-semibold text-n-slate-12">
        {{ $t('SALES.FOLLOW_UP.TITLE') }}
      </h2>
      <button
        v-if="isAdmin"
        class="px-3 py-1.5 text-xs rounded-lg border border-n-strong text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
        @click="toggleScope"
      >
        {{ showAll ? $t('SALES.FOLLOW_UP.MY_FOLLOWUPS') : $t('SALES.FOLLOW_UP.ALL_FOLLOWUPS') }}
      </button>
    </div>

    <!-- Loading -->
    <div v-if="followUpsStore.isFetching" class="flex justify-center py-12">
      <span class="i-lucide-loader-circle animate-spin text-3xl text-n-slate-9" />
    </div>

    <template v-else>
      <!-- Empty state -->
      <div
        v-if="followUpsStore.totalCount === 0"
        class="flex flex-col items-center gap-2 py-12 text-n-slate-9"
      >
        <span class="i-lucide-calendar-check text-4xl" />
        <p class="text-sm">{{ $t('SALES.FOLLOW_UP.EMPTY') }}</p>
      </div>

      <!-- Groups -->
      <template v-for="group in groups" :key="group.key">
        <div v-if="followUpsStore.groups[group.key]?.length" class="flex flex-col gap-2">
          <button
            class="flex items-center gap-2 w-full text-left rounded-lg px-3 py-1.5 hover:bg-n-alpha-2 transition-colors"
            @click="toggleGroup(group.key)"
          >
            <span :class="[group.icon, group.color, 'text-base shrink-0']" />
            <span class="text-sm font-semibold flex-1" :class="group.color">
              {{ group.label }} ({{ followUpsStore.groups[group.key].length }})
            </span>
            <span
              class="i-lucide-chevron-down text-sm text-n-slate-9 transition-transform duration-200"
              :class="{ 'rotate-180': collapsed[group.key] }"
            />
          </button>

          <div
            v-show="!collapsed[group.key]"
            v-for="followUp in followUpsStore.groups[group.key]"
            :key="followUp.id"
            class="rounded-xl border border-n-weak bg-n-alpha-2 px-4 py-3 flex flex-col gap-2"
          >
            <!-- Contato + data -->
            <div class="flex items-start justify-between gap-2">
              <div class="flex flex-col gap-0.5 min-w-0">
                <span class="text-sm font-medium text-n-slate-12 truncate">
                  {{ followUp.contact_name || '#' + followUp.conversation_id }}
                </span>
                <span v-if="followUp.contact_phone" class="text-xs text-n-slate-9">
                  {{ followUp.contact_phone }}
                </span>
              </div>
              <span class="text-xs text-n-slate-9 shrink-0">
                {{ formatDate(followUp.scheduled_for) }}
              </span>
            </div>

            <!-- Agente responsável (destaque para admin) -->
            <div v-if="isAdmin && followUp.agent_name" class="flex items-center gap-1.5">
              <span class="i-lucide-user-round text-xs text-n-slate-9" />
              <span class="text-xs font-medium text-n-slate-11">{{ followUp.agent_name }}</span>
            </div>

            <!-- Motivo da não-venda / fonte -->
            <div
              v-if="followUp.source_reason"
              class="rounded-lg bg-n-alpha-3 border border-n-weak px-3 py-2 text-xs text-n-slate-11"
            >
              <span class="font-medium text-n-slate-9 mr-1">Motivo:</span>{{ followUp.source_reason }}
            </div>

            <!-- Observações do follow-up (se preenchidas) -->
            <div v-if="followUp.notes" class="text-xs text-n-slate-10 italic">
              "{{ followUp.notes }}"
            </div>

            <!-- Etiquetas ativas -->
            <div
              v-if="conversationLabels[followUp.id]?.length"
              class="flex flex-wrap gap-1.5"
            >
              <span
                v-for="label in conversationLabels[followUp.id]"
                :key="label"
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium text-white"
                :style="{ backgroundColor: getLabelColor(label) }"
              >
                {{ label }}
              </span>
            </div>

            <div class="flex items-center flex-wrap gap-2 pt-1">
              <button
                class="text-xs px-2.5 py-1 rounded-lg bg-n-brand text-white hover:brightness-110 transition-all disabled:opacity-50 flex items-center gap-1"
                :disabled="creatingId === followUp.id"
                @click="createNewConversation(followUp)"
              >
                <span
                  :class="creatingId === followUp.id ? 'i-lucide-loader-circle animate-spin' : 'i-lucide-message-circle-plus'"
                  class="text-xs"
                />
                {{ $t('SALES.FOLLOW_UP.NEW_CONVERSATION') }}
              </button>
              <button
                class="text-xs px-2.5 py-1 rounded-lg border border-n-strong text-n-slate-11 hover:bg-n-alpha-3 transition-colors flex items-center gap-1"
                @click="goToConversation(followUp.conversation_id)"
              >
                <span class="i-lucide-history text-xs" />
                {{ $t('SALES.FOLLOW_UP.VIEW_HISTORY') }}
              </button>
              <button
                class="text-xs px-2.5 py-1 rounded-lg border border-n-strong text-n-slate-11 hover:bg-n-alpha-2 transition-colors flex items-center gap-1"
                @click="handleMarkDone(followUp.id)"
              >
                <span class="i-lucide-check text-xs" />
                {{ $t('SALES.FOLLOW_UP.DONE') }}
              </button>
              <button
                class="text-xs px-2.5 py-1 rounded-lg text-n-slate-9 hover:text-n-slate-12 transition-colors"
                @click="handleCancel(followUp.id)"
              >
                {{ $t('SALES.FOLLOW_UP.CANCEL_FOLLOWUP') }}
              </button>

              <!-- Botão etiquetar -->
              <div
                v-if="followUp.conversation_id"
                class="relative ml-auto"
                v-on-click-outside="() => { if (activeLabelDropdown === followUp.id) activeLabelDropdown = null }"
              >
                <button
                  class="text-xs px-2.5 py-1 rounded-lg border border-n-strong text-n-slate-11 hover:bg-n-alpha-3 transition-colors flex items-center gap-1"
                  :class="{ 'bg-n-alpha-3': activeLabelDropdown === followUp.id }"
                  @click="openLabelDropdown(followUp)"
                >
                  <span class="i-lucide-tag text-xs" />
                  {{ $t('SALES.FOLLOW_UP.LABEL') }}
                </button>

                <div
                  v-if="activeLabelDropdown === followUp.id"
                  class="absolute bottom-full mb-1 ltr:left-0 rtl:right-0 z-30 w-48 rounded-xl border border-n-strong bg-n-surface-1 shadow-lg overflow-hidden"
                >
                  <div v-if="!allLabels.length" class="px-3 py-2.5 text-xs text-n-slate-9">
                    {{ $t('SALES.FOLLOW_UP.NO_LABELS') }}
                  </div>
                  <button
                    v-for="label in allLabels"
                    :key="label.id"
                    class="flex items-center gap-2 w-full px-3 py-2 text-xs text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
                    :disabled="updatingLabelFollowUpId === followUp.id"
                    @click="toggleLabel(followUp, label.title)"
                  >
                    <span
                      class="w-2.5 h-2.5 rounded-full shrink-0 border border-white/20"
                      :style="{ backgroundColor: label.color }"
                    />
                    <span class="truncate flex-1 text-left">{{ label.title }}</span>
                    <span
                      v-if="conversationLabels[followUp.id]?.includes(label.title)"
                      class="i-lucide-check text-xs text-n-brand shrink-0"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>
